/**
 * @fileoverview How the locations page is looked at: which density, and what is typed in
 * the search box. The plant page has the same shape with more knobs; this is
 * the same idea with two.
 *
 * The search is the server's (W24 Job H): what is typed is sent, after the
 * usual typing pause, as GET /locations?status=1&q= (useBedSearch), and the
 * page draws the beds it answers (a bed's name, description or its plants'
 * names, removed ones included). Nothing is matched here; the density stays on the phone.
 * A search that failed is handed back as its error (useBedSearch), for the
 * page to say the standard sentence (reported to Sentry) in place of the beds,
 * rather than quietly showing every bed.
 *
 * Where: LocationGrid and LocationToolbar.
 * API: GET /locations?status=1&q=&lang= (searchBeds, through useBedSearch).
 * Settings: LOCATION_VIEW_STORAGE_KEY (config/tunables/garden), through
 * shared/lib/viewMemory; the search's answers are kept under the beds' key
 * (BEDS_KEY, shared/hooks/useBeds) with the language, 'search' and the text,
 * so every change to a bed reads them again.
 *
 * @module locationViews
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { searchBeds, type Location } from '@/shared/api/locations';
import type { ViewName } from '@/shared/components/ViewSwitch';
import { recallView, rememberView } from '@/shared/lib/viewMemory';
import { LOCATION_VIEW_STORAGE_KEY } from '@/config/tunables/garden';
import { BEDS_KEY } from '@/shared/hooks/useBeds';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePaused } from '@/features/what-to-plant/useWhatToPlantSearch';

/** How the locations page is being looked at: its density and its search. */
export interface LocationViewSettings {
  /** The density: cards, grid or list. */
  view: ViewName;
  /** What is typed in the search box; the server finds the beds (useBedSearch). */
  search: string;
}

/** Cards, and nothing searched for: the page as a first visit sees it. */
export const DEFAULT_LOCATION_VIEW: LocationViewSettings = { view: 'cards', search: '' };

/**
 * Whether a remembered value is a density this page still has, so a stale one is dropped.
 *
 * @param value - What storage held.
 */
const isView = (value: unknown): value is ViewName =>
  value === 'cards' || value === 'grid' || value === 'list';

/**
 * The density this browser last used here; the search box always starts empty.
 *
 * @returns The settings to open on.
 */
export function loadLocationView(): LocationViewSettings {
  return { ...DEFAULT_LOCATION_VIEW, ...recallView<LocationViewSettings>(LOCATION_VIEW_STORAGE_KEY, { view: isView }) };
}

/**
 * Remembers the density for next time; the search is not kept, so the page never opens filtered.
 *
 * @param settings - The settings now.
 */
export function saveLocationView(settings: LocationViewSettings): void {
  rememberView(LOCATION_VIEW_STORAGE_KEY, { view: settings.view });
}

/** The Beds search's answer, as the page draws it. */
export interface BedSearch {
  /** The beds the server found, in the list's order; null while nothing is typed, or before the first answer. */
  found: Location[] | null;
  /** The search's failure, or null: the page then says the standard sentence in place of the beds. */
  error: Error | null;
}

/**
 * The Beds search, asked of the server once typing pauses: the beds it found, or null while nothing is typed (spaces
 * only count as nothing). The last answer stays while the next is on its way; before the first, null (every bed).
 *
 * @param search - What is typed.
 * @returns The beds the server found (or null for no search) and the search's failure (or null).
 */
export function useBedSearch(search: string): BedSearch {
  const { i18n: { language } } = useTranslation();
  const text = usePaused(search).trim();
  const { data, error } = useQuery<Location[], Error>({
    queryKey: [BEDS_KEY, language, 'search', text],
    queryFn: () => searchBeds(text),
    enabled: text.length > 0,
    placeholderData: keepPreviousData,
  });
  if (text.length === 0) return { found: null, error: null };
  return { found: data ?? null, error: error ?? null };
}

/**
 * The beds to draw, on a copy, in the order they came: every bed, or, with a search, the beds the server found.
 *
 * @param locations - The beds, or nothing before they load.
 * @param found - The search's answer (useBedSearch), or null for no search.
 * @returns The beds to draw.
 */
export function arrangeLocations(
  locations: Location[] | null | undefined,
  found: Location[] | null,
): Location[] {
  if (found) return [...found];
  return locations ? [...locations] : [];
}
